import { useContext } from 'react';
import { Button } from '@clay-run/glaze/button';
import { Icon } from '@clay-run/glaze/icon';
import { Text } from '@clay-run/glaze/text';
import { Tooltip, TooltipPanel, TooltipTrigger } from '@clay-run/glaze/tooltip';
import { noteTargetKey, type NoteTarget } from '@shared/flow/notes';
import { useNotesEnabled } from '../../../../hooks/useNotesEnabled';
import { FLOW_ICONS } from '../../icons';
import { useOpenNotes } from './useOpenNotes';
import { NotesSummariesContext, notesCountLabels } from './useNotesSummaries';

export interface NotesBubbleProps {
  target: NoteTarget;
  /** Composer prefill (e.g. `@Name `) handed to the rail when opened. */
  prefill?: string;
  /** The record's existing mirror notes text. Only used to choose the no-summary fallback; never parsed for a count. */
  mirrorInput?: string;
}

/**
 * Row entry point for the Notes thread: notes icon + count, with a dot while
 * the server says the thread is unread. Reads the table-provided summaries Map
 * (`NotesSummariesContext`), so it makes zero requests of its own. Companies
 * rows are `<Link>`s, so the click both prevents default and stops
 * propagation. Renders nothing while Notes is disabled.
 */
export function NotesBubble({ target, prefill, mirrorInput }: NotesBubbleProps) {
  const { enabled } = useNotesEnabled();
  const summaries = useContext(NotesSummariesContext);
  const openNotes = useOpenNotes();
  if (!enabled) return null;

  const summary = summaries.get(noteTargetKey(target));
  // No summary yet + existing notes: the count is unknown, so show the icon alone rather than a wrong 0.
  const countUnknown = !summary && !!mirrorInput?.trim();
  const count = summary?.count ?? 0;
  const unread = summary?.unread ?? false;
  const { aria, tip } = notesCountLabels(count, unread, countUnknown);

  return (
    <Tooltip>
      <TooltipTrigger>
        <Button
          size="xs"
          variant="ghost"
          className="relative gap-1"
          aria-label={aria}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            openNotes(target, { prefill, entryPoint: 'row_bubble' });
          }}
        >
          <Icon type={FLOW_ICONS.notes} size="sm" className="text-content-secondary" aria-hidden />
          {!countUnknown && (
            <Text size="xs" variant={count > 0 ? 'secondary' : 'tertiary'} className="tabular-nums">
              {count}
            </Text>
          )}
          {unread && <span aria-hidden className="absolute top-0.5 right-0.5 size-1.5 rounded-full bg-content-action" />}
        </Button>
      </TooltipTrigger>
      <TooltipPanel>{tip}</TooltipPanel>
    </Tooltip>
  );
}
